import React from 'react';
import { connect, ConnectedProps } from '@cerebral/react';
import { state, sequences, props } from 'app/pwa/app.cerebral';

import { sortIndicatorsForChallenge } from 'utils/challenge';

import { Wrapper } from './elements';
import ActCard from 'dc/ActCard';
import ActCardUnavailable from 'dc/ActCardUnavailable';

import {
  useFormatIndicator,
  useText,
  useGetActivityIconUrl,
} from 'common/hooks';

type Props = {
  readonly className: string;
  readonly color?: string;
  readonly activityKey: string;
  readonly timesLogged?: number;
};

const deps = {
  canLog: state.challenge.log.computedCanLog,
  isLoggingSubcategory:
    state.challenge.log.activitySubCategoryLogging[
      state.appContainer.activities[props`activityKey`].subCategory
    ],
  hasRendered: state.appContainer.hasRendered,
  activity: state.appContainer.activities[props`activityKey`],
  campaignIndicator: state.challenge.campaign.indicator,
  isLoggingThisActivity:
    state.challenge.log.activityKeysLogging[props`activityKey`],
  activityCount:
    state.challenge.stats.daily.user[
      state.challenge.campaign.challenge.startOfDays[
        state.challenge.log.selectedDayIndex
      ]
    ].activitiesCount[props`activityKey`],
  toggleLogActivityClicked: sequences`challenge.log.toggleLogActivityClicked`,
  activityInfoClicked: sequences`challenge.log.activityInfoClicked`,
};

function ActivityCard({
  className,
  color,
  timesLogged,
  isLoggingSubcategory,
  canLog,
  hasRendered,
  activityKey,
  activity,
  campaignIndicator,
  isLoggingThisActivity,
  activityCount,
  toggleLogActivityClicked,
  activityInfoClicked,
}: Props & typeof deps & ConnectedProps): React.ReactElement {
  const t = useText();
  const getActivityIconUrl = useGetActivityIconUrl();
  const formatIndicator = useFormatIndicator();

  const indicators = sortIndicatorsForChallenge(
    activity,
    campaignIndicator
  ).map(indicatorObject => ({
    ...indicatorObject,
    value:
      indicatorObject.isCompetingIndicator && indicatorObject.value
        ? formatIndicator(indicatorObject.value, indicatorObject.key)
        : '',
  }));

  if (activity) {
    return (
      <Wrapper className={className}>
        <ActCard
          ariaLabelLogButton={t('a11y.button_labels.log_activity')}
          ariaDescription={t('a11y.button_labels.learn_more')}
          title={activity?.title ?? 'No'}
          ctaText={t(`challenge.activities.learn_more`)}
          indicators={indicators}
          categoryColor={color}
          timesLogged={timesLogged}
          activityIcon={getActivityIconUrl(activity?.icon)}
          activityIconAlt={''}
          onActivityClick={() => {
            if (hasRendered) activityInfoClicked({ activityKey });
          }}
          disabled={(!canLog || isLoggingSubcategory) && !isLoggingThisActivity}
          checked={activityCount > 0}
          onLogClick={() =>
            toggleLogActivityClicked({
              activityKey,
              categoryKey: activity.category,
            })
          }
          isLogging={isLoggingThisActivity}
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper className={className}>
      <ActCardUnavailable />
    </Wrapper>
  );
}

export default connect<Props, typeof deps>(deps, ActivityCard);
